import { type Dictionary, type EntityState } from '@ngrx/entity';

import { type ElementsState } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElement,
  type CanvasElementId,
  type CanvasElementType,
  type PropsOf,
  type SizeSettings,
  type SlotId,
  type VisibilityElementProps,
} from '@blaze-runtime/engine-domain';
import { getNamespaceId, getNewConnectionId } from '@blaze-runtime/engine-domain';
import {
  type ContainerActionConfig,
  getActiveBinding,
  type PropertiesConfigInputContainerItemActionModel,
  type ViewportMode,
  type WithRowActions,
} from '@blaze-shared/elements-domain';
import { GET_TIMEZONES_API_ID } from '@blaze-shared/platform-data-access';
import { isActionEventType } from '@blaze-shared/schema-domain';
import { type RuntimeEventType } from '@blaze-shared/schema-domain';
import { type Nullish } from '@blaze-shared/shared-util';
import {
  type Connection,
  isNavigateNode,
  isOpenDialogNode,
  type Workflow,
} from '@blaze-shared/workflow-domain';

import { type CanvasElementDefinition } from '../../canvas-elements/element-definition';
import {
  arePropsWithData,
  arePropsWithDirty,
  hasDataProps,
  hasProps,
  hasWrapperRefs,
  isSlotIdOfContainer,
} from '../../canvas-elements/element-model/element-props-guards';
import {
  isElementType,
  isVariantOfContainer,
  isVariantOfFormContainer,
} from '../../canvas-elements/element-model/element-type-guards';
import { getFormInputNameByType } from '../../canvas-elements/element-model/io';
import { getElementSlotsCount } from '../../canvas-elements/element-model/slots';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';
import { isElementGeneratedFieldOfFormLike } from '../canvas-element/bindings.utils';
import { getUiRuntimeElement, getWorkflowRuntimeElement } from '../runtime';

export const updateOptionsFields = (
  context: { labelField: string; valueField: string },
  value = 'value',
  label = 'label',
): void => {
  context.labelField = label;
  context.valueField = value;
};

export function elementsToEntityState(elements: CanvasElement[]): EntityState<CanvasElement> {
  const ids: string[] = [];
  const entities: Record<string, CanvasElement> = {};
  for (const element of elements) {
    ids.push(element.id);
    entities[element.id] = element;
  }
  return { ids, entities };
}

export function entityStateToElements(entityState: EntityState<CanvasElement>): CanvasElement[] {
  return entityState.ids.map((id) => entityState.entities[id] as CanvasElement);
}

export const findWrapperParentId = (
  elementsState: EntityState<CanvasElement>,
  elementId: CanvasElementId | undefined,
  elementPredicate?: (el: CanvasElement | undefined) => boolean,
  refIdPredicate?: (refId: string) => boolean,
): string | undefined =>
  (elementsState.ids as string[]).find((id: string) => {
    if (!elementId) {
      return undefined;
    }
    const element = elementsState.entities[id];
    const wrapperRefs = getWrapperRefs(element);
    return (
      (!elementPredicate || elementPredicate(element)) &&
      hasWrapperRefs(element) &&
      wrapperRefs &&
      Object.entries(wrapperRefs).find(
        ([refId, refElementId]) =>
          refElementId === elementId && (!refIdPredicate || refIdPredicate(refId)),
      )
    );
  });

//#region isElementReferencingPageId

const ELEMENTS_TRIGGERING_PAGES: CanvasElementType[] = [
  'button',
  'formContainer',
  'formDetails',
  'scheduler',
  'list',
  'table',
];

export const isElementReferencingPageId = (
  element: CanvasElement,
  pageId: string,
  workflows: Workflow[],
): boolean => {
  if (!hasProps(element) || !ELEMENTS_TRIGGERING_PAGES.includes(element.type)) {
    return false;
  }

  const workflowsToCheck: string[] = [];
  switch (element.type) {
    case 'button': {
      const buttonProps = element.props as PropsOf<'button'>;
      checkComponentAction(buttonProps.content?.action, workflowsToCheck);
      break;
    }
    case 'scheduler': {
      const schedulerProps = element.props as PropsOf<'scheduler'>;
      checkComponentAction(schedulerProps.content?.action, workflowsToCheck);
      break;
    }
    case 'formContainer':
    case 'formDetails': {
      const formProps = element.props as PropsOf<'formContainer'>;
      checkCustomActions(formProps.content?.formActions, workflowsToCheck);
      checkCustomActions(formProps.content?.headerActions, workflowsToCheck);
      break;
    }
    case 'list': {
      const listProps = element.props as PropsOf<'list'>;
      if (
        checkCrudActions(pageId, listProps?.content as WithRowActions | undefined, workflowsToCheck)
      ) {
        return true;
      }

      checkCustomActions(listProps.content?.actions?.items, workflowsToCheck);
      break;
    }
    case 'table': {
      const tableProps = element.props as PropsOf<'table'>;
      if (
        checkCrudActions(
          pageId,
          tableProps?.content as WithRowActions | undefined,
          workflowsToCheck,
        )
      ) {
        return true;
      }

      checkCustomActions(tableProps.content?.rowActions, workflowsToCheck);
      break;
    }
  }

  for (const wfId of workflowsToCheck) {
    const wf = workflows.find(({ id }) => id === wfId);
    if (wf && checkWorkflow(pageId, wf)) {
      return true;
    }
  }

  return false;
};

export function getElementFriendlyName(
  elementDefinition?: CanvasElementDefinition,
  element?: CanvasElement,
  slotId?: Nullish<SlotId>,
): string {
  const defName = elementDefinition?.name || '';
  if (!isVariantOfContainer(element) || !slotId) {
    return defName;
  }
  return getElementSlotsCount(element) > 1 ? 'Column' : defName;
}

function checkCrudActions(
  pageId: string,
  contentProps: WithRowActions | undefined,
  workflowsToCheck: string[],
): boolean {
  for (const action of ['itemAdd', 'itemEdit', 'itemView'] as const) {
    const { targetType, targetId } = contentProps?.[action] || {};
    if (
      (targetType === 'Page' || targetType === 'Dialog' || targetType === 'Drawer') &&
      targetId === pageId
    ) {
      return true;
    } else if (targetType === 'Workflow' && targetId) {
      workflowsToCheck.push(targetId);
    }
  }
  return false;
}

function checkComponentAction(
  action: ContainerActionConfig | undefined,
  workflowsToCheck: string[],
): void {
  if (!action?.workflowId) {
    return;
  }

  workflowsToCheck.push(action.workflowId);
}

function checkCustomActions(
  actions: PropertiesConfigInputContainerItemActionModel[] | undefined,
  workflowsToCheck: string[],
): void {
  if (!actions) {
    return;
  }

  workflowsToCheck.push(...(actions.map((a) => a.action?.workflowId).filter(Boolean) as string[]));
}

function checkWorkflow(pageId: string, workflow: Workflow): boolean {
  for (const node of workflow.nodes) {
    if (isNavigateNode(node) && node.target === pageId) {
      return true;
    } else if (isOpenDialogNode(node) && node.dialogId === pageId) {
      return true;
    }
  }
  return false;
}

//#endregion

export function shouldShowEmptyDataMessage(props?: VisibilityElementProps): boolean {
  return !props?.content?.visible?.internalStates?.includes('hideWhenNoData');
}

export function isTimezoneDropdown(element: CanvasElement | undefined): boolean {
  if (!element || element.type !== 'dropdown' || !hasDataProps(element)) {
    return false;
  }
  const dataSource = getActiveBinding(element.props.data, 'optionsList')?.dataSource;
  return dataSource?.id === GET_TIMEZONES_API_ID;
}

export function shouldConfirmElementDelete(
  elementsState: Pick<ElementsState, 'entities'>,
  element?: CanvasElement,
  slotId?: SlotId,
): number | boolean | undefined {
  // Check if removed element is an automatically generated Form or Details View field (which can be easily re-added in the form's properties editor)
  if (element && isElementGeneratedFieldOfFormLike(elementsState, element.id)) {
    return false;
  }

  if (!isVariantOfContainer(element)) {
    return true;
  }

  if (isSlotIdOfContainer(element, slotId)) {
    const children = (element.slots[slotId]?.children ?? []).filter(
      (childId) => !isElementType(elementsState.entities[childId], 'breakLine'),
    );

    return !!children.length;
  }

  return element.slots.ids.some((id) => {
    const slot = element.slots[id];
    return slot?.children.length;
  });
}

export const getElementToWorkflowConnection = (
  elementId: CanvasElementId,
  workflowId: string,
  elementType?: CanvasElementType,
  eventType?: RuntimeEventType,
): Connection => {
  const id = getNewConnectionId(getNamespaceId(elementId));

  if (!eventType) {
    switch (elementType) {
      case 'audioRecorder':
      case 'formContainer':
      case 'formDetails':
      case 'uploadFile':
        eventType = 'change';
        break;
      case 'button':
      case 'paymentButton':
      case 'notificationButton':
        eventType = 'click';
        break;
      case 'wizard':
        eventType = 'submit';
        break;
      default:
        eventType = 'action';
        break;
    }
  }

  const connection = {
    id,
    eventType,
    source: getUiRuntimeElement(elementId),
    target: getWorkflowRuntimeElement(workflowId),
  };

  if (isActionEventType(connection.eventType)) {
    connection.source.output = 'events';
  }

  return connection;
};

export function checkViewportVisibility(
  currentViewport?: ViewportMode,
  shouldBeVisibleOnViewports?: ViewportMode[],
): boolean {
  if (!shouldBeVisibleOnViewports || shouldBeVisibleOnViewports.length === 0) {
    return true;
  }
  return shouldBeVisibleOnViewports.some((viewport) => viewport === currentViewport);
}

export function getElementSizeOverride(
  element: CanvasElement | undefined,
  elements: Dictionary<CanvasElement>,
): SizeSettings | undefined {
  if (!element) {
    return undefined;
  }
  // size overrides apply even when element has no props
  const props = hasProps(element) ? element.props : undefined;
  const dirty = arePropsWithDirty(props) ? props.dirty : undefined;
  if ((!dirty?.width || !dirty.height) && props && arePropsWithData(props)) {
    const binding = getActiveBinding(props.data, getFormInputNameByType(element.type));
    if (binding?.dataSource?.sourceType === 'formContainer') {
      const form = elements[binding.dataSource?.id];
      const size = isVariantOfFormContainer(form) ? form.props.content.children?.size : undefined;
      const result: SizeSettings = {};
      if (!dirty?.width && size?.width) {
        result.width = size.width;
      }
      if (!dirty?.height && size?.height) {
        result.height = size.height;
      }
      return result;
    }
  }
  return undefined;
}
